import { DestroyRef, inject, Injectable } from '@angular/core';
import { TaskSdkService } from '@vendasta/task';
import {
  SearchTaskRequestInterface,
  SearchTaskResponse,
  SortDirection,
  SortInterface,
  Task,
  TaskManagerApiService,
} from '@vendasta/task-manager';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map, Observable, switchMap, tap } from 'rxjs';
import { CardItem, CardSubtitle, ProjectType, StatusBadge, Statuses, TaskAccess, TaskStatus } from './interfaces';
import { differenceInCalendarDays, isBefore } from 'date-fns';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable()
export class CrmFulfillmentProjectsAssociationCardService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly taskManagerService = inject(TaskManagerApiService);

  private readonly partnerId$$ = new BehaviorSubject('');
  private readonly accountGroupId$$ = new BehaviorSubject('');
  private readonly currentCursor$$ = new BehaviorSubject('');
  private readonly nextCursor$$ = new BehaviorSubject('');
  private readonly hasMore$$ = new BehaviorSubject(false);
  private readonly items$$ = new BehaviorSubject<Task[]>([]);

  readonly items$ = this.items$$.pipe(map((items) => items.map((item) => this.mapTaskToCardItem(item))));
  readonly hasMore$ = this.hasMore$$.asObservable();
  readonly hasItems$ = this.items$$.pipe(map((items) => items?.length > 0));
  readonly pageSize = 5;

  constructor() {
    this.setupFetchers();
  }

  setPartnerId(partnerId: string): void {
    this.partnerId$$.next(partnerId);
  }

  setAccountGroupId(accountGroupId: string): void {
    this.accountGroupId$$.next(accountGroupId);
  }

  fetchNext(): void {
    this.currentCursor$$.next(this.nextCursor$$.getValue());
  }

  private setupFetchers(): void {
    const namespace$ = combineLatest([this.partnerId$$, this.accountGroupId$$]).pipe(
      distinctUntilChanged(),
      tap(() => this.resetList()),
      filter(([partnerId, accountGroupId]) => !!partnerId && !!accountGroupId),
      map(([partnerId, accountGroupId]) => TaskSdkService.buildAccountGroupNamespace(partnerId, accountGroupId)),
    );
    const fetcher$ = combineLatest([namespace$, this.currentCursor$$]).pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(([namespace, cursor]) => this.fetchProjects(namespace, cursor, this.pageSize)),
    );
    fetcher$.subscribe((response) => {
      this.nextCursor$$.next(response?.nextCursor || '');
      this.hasMore$$.next(response?.hasMore);
      this.items$$.next([...this.items$$.getValue(), ...(response.tasks || [])]);
    });
  }

  private resetList(): void {
    this.nextCursor$$.next('');
    this.hasMore$$.next(false);
    this.items$$.next([]);
  }

  private fetchProjects(namespace: string, cursor: string, pageSize: number): Observable<SearchTaskResponse> {
    const sortBy: SortInterface[] = [
      { field: 'due_date', direction: SortDirection.SORT_DIRECTION_DESCENDING },
      { field: 'title.raw', direction: SortDirection.SORT_DIRECTION_ASCENDING },
    ];
    const request: SearchTaskRequestInterface = {
      cursor: cursor,
      pageSize: pageSize,
      status: Statuses,
      access: TaskAccess,
      excludeHistory: true,
      types: ProjectType,
      sorts: sortBy,
      namespace,
    };
    return this.taskManagerService.search(request);
  }

  private mapTaskToCardItem(task: Task): CardItem<Task> {
    const subtitle: CardSubtitle =
      task.status === TaskStatus.COMPLETED
        ? { label: 'ASSOCIATIONS.FULFILLMENT_PROJECTS.SUBTITLE.COMPLETED', date: task.completionDate }
        : { label: 'ASSOCIATIONS.FULFILLMENT_PROJECTS.SUBTITLE.DUE', date: task.dueDate };

    return {
      id: task.identity.taskId,
      title: task.title,
      subtitle,
      badge: this.statusBadge(task),
      url: this.buildProjectUrl(task.identity.taskId, this.accountGroupId$$.value),
      data: task,
    };
  }

  private statusBadge(task: Task): StatusBadge {
    switch (task.status) {
      case TaskStatus.IN_PROGRESS:
        return this.isOverdue(task)
          ? { label: 'ASSOCIATIONS.FULFILLMENT_PROJECTS.STATUS.OVERDUE', color: 'yellow' }
          : { label: 'ASSOCIATIONS.FULFILLMENT_PROJECTS.STATUS.IN_PROGRESS', color: 'blue' };
      case TaskStatus.OPEN: {
        return this.isOverdue(task)
          ? { label: 'ASSOCIATIONS.FULFILLMENT_PROJECTS.STATUS.OVERDUE', color: 'yellow' }
          : { label: 'ASSOCIATIONS.FULFILLMENT_PROJECTS.STATUS.OPEN', color: 'blue' };
      }
      case TaskStatus.WAITING_ON_CUSTOMER: {
        return this.isOverdue(task)
          ? { label: 'ASSOCIATIONS.FULFILLMENT_PROJECTS.STATUS.OVERDUE', color: 'yellow' }
          : { label: 'ASSOCIATIONS.FULFILLMENT_PROJECTS.STATUS.WAITING_ON_CUSTOMER', color: 'yellow' };
      }
      case TaskStatus.COMPLETED: {
        return { label: 'ASSOCIATIONS.FULFILLMENT_PROJECTS.STATUS.COMPLETED', color: 'green' };
      }
      default: {
        // custom status
        return { label: task.status, color: 'grey' };
      }
    }
  }

  private buildProjectUrl(taskId: string, accountGroupId: string): string {
    return `/task-manager/reporting/accounts/${accountGroupId}/projects/${taskId}`;
  }

  private isOverdue(task: Task): boolean {
    if (isBefore(task.dueDate, Date.now())) {
      // only considered as overdue after the EOD
      const dueDays = Math.abs(differenceInCalendarDays(Date.now(), task.dueDate));
      return dueDays > 0;
    }
    return false;
  }
}
